import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, GitCommitVertical, Plus } from 'lucide-react';
import {
  Button,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  useToast,
  type SelectOption,
} from '../../ui';
import { useTimeline } from '../../features/timeline/useTimeline';
import type { TimelineScope } from '../../features/timeline/types';
import { TimelineView } from './TimelineView';
import { TimelineEventModal } from './TimelineEventModal';

const SCOPE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'Whole team' },
  { value: 'goal', label: 'By goal' },
  { value: 'workstream', label: 'By workstream' },
  { value: 'service', label: 'By service' },
];

function readInitialScope(params: URLSearchParams): TimelineScope {
  const type = params.get('scope');
  const id = params.get('id') ?? undefined;
  if (type === 'goal' || type === 'workstream' || type === 'service') {
    return { type, id };
  }
  return { type: 'all' };
}

/**
 * Engineering Timeline — a chronological, cross-entity story derived from
 * workstreams, documents, decisions, blockers and milestones, scoped to the
 * whole team or a single goal / workstream / service, plus optional manual
 * events.
 */
export function TimelinePage() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const initialScope = useMemo(() => readInitialScope(searchParams), [searchParams]);

  const {
    events,
    scope,
    setScope,
    goals,
    workstreams,
    services,
    documents,
    decisions,
    tasks,
    goalTitles,
    workstreamTitles,
    serviceTitles,
    storyTitles,
    currentAlias,
    loading,
    error,
    createEvent,
    removeEvent,
  } = useTimeline(initialScope);

  const [modalOpen, setModalOpen] = useState(false);

  const entityOptions: SelectOption[] = useMemo(() => {
    if (scope.type === 'goal') return goals.map((g) => ({ value: g.id, label: g.title || g.id }));
    if (scope.type === 'workstream') return workstreams.map((w) => ({ value: w.id, label: w.title || w.id }));
    if (scope.type === 'service') return services.map((s) => ({ value: s.id, label: s.name || s.id }));
    return [];
  }, [scope.type, goals, workstreams, services]);

  const onSubmit = async (draft: Parameters<typeof createEvent>[0]) => {
    await createEvent(draft);
    toast({ tone: 'success', title: 'Event added', description: draft.title });
  };
  const onDeleteEvent = async (id: string) => {
    await removeEvent(id);
    toast({ tone: 'success', title: 'Event removed' });
  };

  const needsEntity = scope.type !== 'all' && !scope.id;

  return (
    <div>
      <PageHeader
        icon={<GitCommitVertical className="h-5 w-5" />}
        title="Engineering Timeline"
        description="What happened, in order — derived from workstreams, docs, decisions, blockers and milestones."
        actions={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
            Add event
          </Button>
        }
      />

      {/* Scope picker. */}
      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Select
            label="Scope"
            value={scope.type}
            options={SCOPE_TYPE_OPTIONS}
            onChange={(e) =>
              setScope({ type: e.target.value as TimelineScope['type'], id: undefined })
            }
          />
        </div>
        {scope.type !== 'all' && (
          <div className="w-64">
            <Select
              label={scope.type === 'service' ? 'Service' : scope.type === 'goal' ? 'Goal' : 'Workstream'}
              placeholder="Choose…"
              value={scope.id ?? ''}
              options={entityOptions}
              onChange={(e) => setScope({ type: scope.type, id: e.target.value || undefined })}
            />
          </div>
        )}
        <span className="ml-auto self-center text-xs text-zinc-500">
          {events.length} {events.length === 1 ? 'event' : 'events'}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Building timeline" />
        </div>
      ) : error ? (
        <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Couldn't build the timeline" description={error} />
      ) : needsEntity ? (
        <EmptyState
          icon={<GitCommitVertical className="h-6 w-6" />}
          title={`Choose a ${scope.type}`}
          description="Pick a specific entity above to see its derived timeline."
        />
      ) : events.length === 0 ? (
        <EmptyState
          icon={<GitCommitVertical className="h-6 w-6" />}
          title="No events yet"
          description="As workstreams progress and docs, decisions, blockers and milestones are captured, they'll appear here. You can also add a manual event."
          action={
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setModalOpen(true)}>
              Add event
            </Button>
          }
        />
      ) : (
        <TimelineView
          events={events}
          goalTitles={goalTitles}
          workstreamTitles={workstreamTitles}
          serviceTitles={serviceTitles}
          storyTitles={storyTitles}
          onDeleteEvent={onDeleteEvent}
        />
      )}

      <TimelineEventModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultEngineer={currentAlias}
        goals={goals}
        workstreams={workstreams}
        services={services}
        documents={documents}
        decisions={decisions}
        tasks={tasks}
        onSubmit={onSubmit}
      />
    </div>
  );
}

export default TimelinePage;
